import { useState, useEffect } from 'react'
import { ref, onValue, set, push, remove } from 'firebase/database'
import { database } from '../config/firebase'
import { ContactFormData } from '../types'
import styles from './Admin.module.css'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

const ADMIN_USERNAME = 'admin'
const ADMIN_PASSWORD = 'üçüzdingiller'

type TabType = 'dashboard' | 'anketler' | 'grafikler' | 'finansal'

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  
  // Contacts state
  const [contacts, setContacts] = useState<Array<ContactFormData & { id: string }>>([])
  const [contactsLoading, setContactsLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set())
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    service: '',
    message: '',
    status: 'Aranmadı',
    teklifUcreti: '',
    kazanilanRakam: ''
  })

  useEffect(() => {
    const loggedIn = localStorage.getItem('adminLoggedIn') === 'true'
    setIsLoggedIn(loggedIn)
    if (loggedIn) {
      loadContacts()
    } else {
      setContactsLoading(false)
    }
  }, [])

  const loadContacts = () => {
    const contactsRef = ref(database, 'contacts')
    
    onValue(contactsRef, (snapshot) => {
      if (!snapshot.exists()) {
        setContacts([])
        setContactsLoading(false)
        return
      }

      const data = snapshot.val()
      const contactsArray = Object.entries(data)
        .map(([key, value]) => ({
          id: key,
          ...(value as ContactFormData)
        }))
        .sort((a, b) => {
          const dateA = new Date(a.timestamp || a.date || 0).getTime()
          const dateB = new Date(b.timestamp || b.date || 0).getTime()
          return dateB - dateA
        })

      setContacts(contactsArray)
      setContactsLoading(false)
    }, (error) => {
      console.error('Error loading contacts:', error)
      setContactsLoading(false)
    })
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      localStorage.setItem('adminLoggedIn', 'true')
      setIsLoggedIn(true)
      setError('')
      loadContacts()
    } else {
      setError('Kullanıcı adı veya şifre hatalı!')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn')
    setIsLoggedIn(false)
    setContacts([])
  }

  const updateContactField = async (contactId: string, field: string, value: string | number) => {
    try {
      const contactRef = ref(database, `contacts/${contactId}/${field}`)
      await set(contactRef, value)
    } catch (error) {
      console.error('Error updating contact:', error)
      alert('Güncelleme sırasında bir hata oluştu. Lütfen tekrar deneyin.')
    }
  }

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newContact.name || !newContact.phone || !newContact.service) {
      alert('Lütfen isim, telefon ve hizmet alanlarını doldurun.')
      return
    }

    try {
      const contactData: ContactFormData = {
        name: newContact.name.trim(),
        phone: newContact.phone.trim(),
        service: newContact.service,
        message: newContact.message.trim() || '',
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleString('tr-TR'),
        status: newContact.status,
        teklifUcreti: newContact.teklifUcreti ? parseFloat(newContact.teklifUcreti) : undefined,
        kazanilanRakam: newContact.kazanilanRakam ? parseFloat(newContact.kazanilanRakam) : undefined
      }

      const contactsRef = ref(database, 'contacts')
      await push(contactsRef, contactData)

      setNewContact({
        name: '',
        phone: '',
        service: '',
        message: '',
        status: 'Aranmadı',
        teklifUcreti: '',
        kazanilanRakam: ''
      })
      setShowAddForm(false)
      alert('Anket başarıyla eklendi!')
    } catch (error) {
      console.error('Error adding contact:', error)
      alert('Anket eklenirken bir hata oluştu.')
    }
  }

  const handleBulkAdd = async () => {
    if (!confirm('19 kayıt eklemek istediğinizden emin misiniz?')) return

    const projects = [
      { url: 'https://www.rosewebdesignpro.com', amount: 5000 },
      { url: 'https://www.ercantarimm.com', amount: 7000 },
      { url: 'https://alyegitim.org', amount: 10000 },
      { url: 'https://marmaradagitim.com', amount: 10000 },
      { url: 'https://www.cetinyolyardim.com', amount: 4000 },
      { url: 'https://www.nakolog.com', amount: 10000 },
      { url: 'https://www.melowajans.com', amount: 10000 },
      { url: 'https://www.multintermodal.com', amount: 10000 },
      { url: 'https://www.gayrethalıyıkama.com', amount: 8000 },
      { url: 'https://www.ariakademi.com.tr', amount: 8000 },
      { url: 'https://www.gulcemalguzellik.com', amount: 20000 },
      { url: 'https://www.e3dglobal.com', amount: 10000 },
      { url: 'https://aselcambalkonsistemleri.com.tr', amount: 10000 },
      { url: 'https://www.esacraft.com', amount: 8000 },
      { url: 'https://www.ozataotokurtarma.com.tr', amount: 5000 },
      { url: 'https://www.sofrafresh.com', amount: 15000 },
      { url: 'https://www.akaydinotocekici.com.tr', amount: 4000 },
      { url: 'https://www.veliogluglobal.com.tr', amount: 8000 },
      { url: 'https://www.kimimarlik.com.tr', amount: 8000 }
    ]

    // Ekim 2025 ile Aralık 2025 arasında tarihleri dağıt
    const startDate = new Date(2025, 9, 1) // 1 Ekim 2025
    const endDate = new Date(2025, 11, 31) // 31 Aralık 2025
    const daysDiff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const interval = daysDiff > 0 ? Math.floor(daysDiff / projects.length) : 1

    try {
      const contactsRef = ref(database, 'contacts')
      
      for (let i = 0; i < projects.length; i++) {
        const project = projects[i]
        const daysOffset = i * interval
        const projectDate = new Date(startDate)
        projectDate.setDate(projectDate.getDate() + daysOffset)
        
        // URL'den domain adını çıkar
        const domain = project.url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
        const name = domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1)

        const contactData: ContactFormData = {
          name: name,
          phone: '-',
          service: 'kurumsal-web',
          message: project.url,
          timestamp: projectDate.toISOString(),
          date: projectDate.toLocaleString('tr-TR'),
          status: 'Tamamlandı',
          kazanilanRakam: project.amount
        }

        await push(contactsRef, contactData)
      }

      alert(`${projects.length} kayıt başarıyla eklendi!`)
      loadContacts()
    } catch (error) {
      console.error('Error adding bulk contacts:', error)
      alert('Kayıtlar eklenirken bir hata oluştu.')
    }
  }

  const handleSelectContact = (contactId: string) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev)
      if (newSet.has(contactId)) {
        newSet.delete(contactId)
      } else {
        newSet.add(contactId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set())
    } else {
      setSelectedContacts(new Set(contacts.map(c => c.id)))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedContacts.size === 0) {
      alert('Lütfen silmek istediğiniz anketleri seçin.')
      return
    }

    if (!confirm(`${selectedContacts.size} anketi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`)) {
      return
    }

    try {
      const deletePromises = Array.from(selectedContacts).map(contactId => {
        const contactRef = ref(database, `contacts/${contactId}`)
        return remove(contactRef)
      })

      await Promise.all(deletePromises)
      const deletedCount = selectedContacts.size
      setSelectedContacts(new Set())
      alert(`${deletedCount} anket başarıyla silindi!`)
      loadContacts()
    } catch (error) {
      console.error('Error deleting contacts:', error)
      alert('Anketler silinirken bir hata oluştu.')
    }
  }

  const getServiceName = (serviceValue: string): string => {
    const services: Record<string, string> = {
      'kurumsal-web': 'Kurumsal Web Sitesi',
      'urun-yonetim': 'İçerik Yönetim Sistemi',
      'e-ticaret': 'E-Ticaret Çözümleri'
    }
    return services[serviceValue] || serviceValue
  }

  // Statistics calculations
  const totalTeklif = contacts
    .filter(c => c.teklifUcreti && c.teklifUcreti > 0)
    .reduce((sum, c) => sum + (c.teklifUcreti || 0), 0)

  const totalKazanilan = contacts
    .filter(c => {
      const status = c.status || 'Aranmadı'
      return c.kazanilanRakam && 
             c.kazanilanRakam > 0 && 
             status !== 'Yarra Yedi' && 
             status !== 'Sipariş Aşamada'
    })
    .reduce((sum, c) => sum + (c.kazanilanRakam || 0), 0)

  const netKar = totalKazanilan - totalTeklif

  // Monthly statistics
  const monthlyStats = contacts.reduce((acc, contact) => {
    const date = new Date(contact.timestamp || contact.date || 0)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const status = contact.status || 'Aranmadı'
    
    if (!acc[monthKey]) {
      acc[monthKey] = { teklif: 0, kazanilan: 0 }
    }
    
    acc[monthKey].teklif += contact.teklifUcreti || 0
    
    // Kazanılan rakama sadece "Yarra Yedi" ve "Sipariş Aşamada" olmayanları ekle
    if (status !== 'Yarra Yedi' && status !== 'Sipariş Aşamada') {
      acc[monthKey].kazanilan += contact.kazanilanRakam || 0
    }
    
    return acc
  }, {} as Record<string, { teklif: number; kazanilan: number }>)

  // Prepare chart data for monthly statistics
  const chartData = Object.entries(monthlyStats)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, stats]) => {
      const [year, monthNum] = month.split('-')
      const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleString('tr-TR', { month: 'short', year: 'numeric' })
      return {
        month: monthName,
        teklif: stats.teklif,
        kazanilan: stats.kazanilan,
        net: stats.kazanilan - stats.teklif
      }
    })

  const contactsStats = {
    total: contacts.length,
    today: contacts.filter(contact => {
      const contactDate = new Date(contact.timestamp || contact.date || 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return contactDate >= today
    }).length,
    withTeklif: contacts.filter(contact => contact.teklifUcreti && contact.teklifUcreti > 0).length,
    withKazanilan: contacts.filter(contact => {
      const status = contact.status || 'Aranmadı'
      return contact.kazanilanRakam && 
             contact.kazanilanRakam > 0 && 
             status !== 'Yarra Yedi' && 
             status !== 'Sipariş Aşamada'
    }).length
  }

  // İlk projeden bugüne kadar geçen süreyi hesapla (ay ve gün)
  const calculateWorkDuration = () => {
    if (contacts.length === 0) return { months: 0, days: 0 }
    
    const dates = contacts
      .map(contact => new Date(contact.timestamp || contact.date || 0))
      .filter(date => !isNaN(date.getTime()))
    
    if (dates.length === 0) return { months: 0, days: 0 }
    
    const earliestDate = new Date(Math.min(...dates.map(d => d.getTime())))
    const today = new Date()
    
    // Tarihleri normalize et (saat bilgilerini sıfırla)
    earliestDate.setHours(0, 0, 0, 0)
    today.setHours(0, 0, 0, 0)
    
    // Toplam gün farkını hesapla
    const totalDays = Math.floor((today.getTime() - earliestDate.getTime()) / (1000 * 60 * 60 * 24))
    
    // Ay ve gün hesaplama
    let months = 0
    let days = 0
    
    if (totalDays > 0) {
      const startYear = earliestDate.getFullYear()
      const startMonth = earliestDate.getMonth()
      const startDay = earliestDate.getDate()
      
      const endYear = today.getFullYear()
      const endMonth = today.getMonth()
      const endDay = today.getDate()
      
      // Ay farkını hesapla
      months = (endYear - startYear) * 12 + (endMonth - startMonth)
      
      // Gün farkını hesapla
      if (endDay >= startDay) {
        days = endDay - startDay
      } else {
        // Önceki ayın son gününü al
        const lastDayOfPrevMonth = new Date(endYear, endMonth, 0).getDate()
        days = lastDayOfPrevMonth - startDay + endDay
        if (months > 0) {
          months--
        }
      }
    }
    
    return { months, days }
  }

  const workDuration = calculateWorkDuration()

  // Toplam çalışma saatini hesapla (her gün 2 saat)
  const calculateTotalHours = () => {
    const totalDays = workDuration.months * 30 + workDuration.days
    const hoursPerDay = 2 // Her gün 2 saat
    return Math.round(totalDays * hoursPerDay)
  }

  const totalHours = calculateTotalHours()

  // Günlük ve aylık ortalama kazanç hesapla
  const calculateAverageEarnings = () => {
    const totalDays = workDuration.months * 30 + workDuration.days
    const totalMonths = workDuration.months + (workDuration.days / 30)
    
    const dailyAverage = totalDays > 0 ? totalKazanilan / totalDays : 0
    const monthlyAverage = totalMonths > 0 ? totalKazanilan / totalMonths : 0
    
    return {
      daily: dailyAverage,
      monthly: monthlyAverage,
      totalDays: totalDays
    }
  }

  const averageEarnings = calculateAverageEarnings()

  // Prepare pie chart data
  const pieData = [
    { name: 'Kazanılan', value: totalKazanilan, color: '#51cf66' },
    { name: 'Teklif', value: totalTeklif, color: '#ff6b6b' }
  ].filter(item => item.value > 0)

  if (!isLoggedIn) {
    return (
      <div className={styles.loginContainer}>
        <div className={styles.loginBox}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img src="/logo.png" alt="3K Digitale" style={{ height: '60px', width: 'auto', marginBottom: '1rem' }} />
          </div>
          <h1>Admin Panel</h1>
          <p>3K Digitale Yönetim Paneli</p>
          <form onSubmit={handleLogin}>
            <div className={styles.formGroup}>
              <label htmlFor="username">Kullanıcı Adı</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">Şifre</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <button type="submit" className={styles.loginBtn}>Giriş Yap</button>
            {error && <div className={styles.errorMessage}>{error}</div>}
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.adminContainer}>
      <div className={styles.adminHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src="/logo.png" alt="3K Digitale" style={{ height: '40px', width: 'auto' }} />
          <h1>3K Digitale Admin Panel</h1>
        </div>
        <button className={styles.logoutBtn} onClick={handleLogout}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          <span>Çıkış Yap</span>
        </button>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'dashboard' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            <span>Dashboard</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'anketler' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('anketler')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            <span>Anketler</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'grafikler' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('grafikler')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            <span>Grafikler</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'finansal' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('finansal')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            <span>Finansal</span>
          </button>
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <div className={styles.dashboardContainer}>
          <div className={styles.dashboardHeader}>
            <h2>Genel Bakış</h2>
            <p>3K Digitale Yönetim Paneli</p>
          </div>

          <div className={styles.quickStatsGrid}>
            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Toplam Anket</h3>
                <div className={styles.quickStatNumber}>{contactsStats.total}</div>
                <div className={styles.quickStatChange}>
                  <span className={styles.positive}>+{contactsStats.today} bugün</span>
                </div>
              </div>
            </div>

            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Toplam Teklif</h3>
                <div className={styles.quickStatNumber}>
                  {totalTeklif.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })}
                </div>
                <div className={styles.quickStatChange}>
                  <span className={styles.positive}>{contactsStats.withTeklif} anket</span>
                </div>
              </div>
            </div>

            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Toplam Kazanılan</h3>
                <div className={styles.quickStatNumber}>
                  {totalKazanilan.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })}
                </div>
                <div className={styles.quickStatChange}>
                  <span className={styles.positive}>{contactsStats.withKazanilan} anket</span>
                </div>
              </div>
            </div>

            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Net Kar</h3>
                <div className={styles.quickStatNumber} style={{ color: netKar >= 0 ? '#51cf66' : '#ff6b6b' }}>
                  {netKar.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })}
                </div>
                <div className={styles.quickStatChange}>
                  <span className={netKar >= 0 ? styles.positive : styles.negative}>
                    {netKar >= 0 ? 'Kârlı' : 'Zararlı'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.quickStatsGrid} style={{ marginTop: '1.5rem' }}>
            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Günlük Ortalama</h3>
                <div className={styles.quickStatNumber}>
                  {averageEarnings.daily.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })}
                </div>
                <div className={styles.quickStatChange}>
                  <span className={styles.positive}>Günlük</span>
                </div>
              </div>
            </div>

            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Aylık Ortalama</h3>
                <div className={styles.quickStatNumber}>
                  {averageEarnings.monthly.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 })}
                </div>
                <div className={styles.quickStatChange}>
                  <span className={styles.positive}>Aylık</span>
                </div>
              </div>
            </div>

            <div className={styles.quickStatCard}>
              <div className={styles.quickStatIcon} style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className={styles.quickStatContent}>
                <h3>Çalışma Süresi</h3>
                <div className={styles.quickStatNumber}>
                  {totalHours.toLocaleString('tr-TR')} Saat
                </div>
                <div className={styles.quickStatChange}>
                  <span className={styles.positive}>
                    Kaydedilen Çalışma Saati: {totalHours.toLocaleString('tr-TR')} Saat
                  </span>
                </div>
              </div>
            </div>
          </div>

          {chartData.length > 0 && (
            <div className={styles.dashboardChartsRow}>
              <div className={styles.dashboardChartCard}>
                <h3>Aylık Trend</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#ffffff"
                      style={{ fontSize: '11px', fontWeight: 600 }}
                    />
                    <YAxis 
                      stroke="#ffffff"
                      style={{ fontSize: '11px', fontWeight: 600 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.95)', 
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                      formatter={(value: number | undefined) => value ? `${value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}` : ''}
                    />
                    <Legend 
                      wrapperStyle={{ color: '#ffffff', fontWeight: 600, fontSize: '12px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="kazanilan" 
                      stroke="#51cf66" 
                      strokeWidth={2}
                      name="Kazanılan"
                      dot={{ fill: '#51cf66', r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="teklif" 
                      stroke="#ff6b6b" 
                      strokeWidth={2}
                      name="Teklif"
                      dot={{ fill: '#ff6b6b', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={styles.dashboardChartCard}>
                <h3>Teklif/Kazanılan Dağılımı</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props: any) => {
                        const { name, percent } = props;
                        return `${name || ''}: ${percent ? (percent * 100).toFixed(0) : 0}%`;
                      }}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.95)', 
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                      formatter={(value: number | undefined) => value ? `${value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}` : ''}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'anketler' && (
        <div className={styles.anketlerContainer}>
          <div className={styles.anketlerHeader}>
            <h2>Anket Yönetimi</h2>
            <div className={styles.headerButtons}>
              <button 
                className={styles.bulkAddButton} 
                onClick={handleBulkAdd}
                title="Ekim 2025 - Aralık 2025 arası projeleri ekle"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
                <span>Toplu Ekle</span>
              </button>
              <button 
                className={styles.addButton} 
                onClick={() => setShowAddForm(!showAddForm)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>{showAddForm ? 'İptal' : 'Yeni Anket Ekle'}</span>
              </button>
            </div>
          </div>

          {showAddForm && (
            <div className={styles.addFormContainer}>
              <form onSubmit={handleAddContact} className={styles.addForm}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>İsim Soyisim *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      required
                      placeholder="İsim Soyisim"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Telefon *</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      required
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Hizmet *</label>
                    <select
                      value={newContact.service}
                      onChange={(e) => setNewContact({ ...newContact, service: e.target.value })}
                      required
                    >
                      <option value="">Hizmet Seçiniz</option>
                      <option value="kurumsal-web">Kurumsal Web Sitesi</option>
                      <option value="urun-yonetim">İçerik Yönetim Sistemi</option>
                      <option value="e-ticaret">E-Ticaret Çözümleri</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Durum</label>
                    <select
                      value={newContact.status}
                      onChange={(e) => setNewContact({ ...newContact, status: e.target.value })}
                    >
                      <option value="Aranmadı">Aranmadı</option>
                      <option value="Arandı">Arandı</option>
                      <option value="Teklif Verildi">Teklif Verildi</option>
                      <option value="Tamamlandı">Tamamlandı</option>
                    </select>
                  </div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Teklif Ücreti (₺)</label>
                    <input
                      type="number"
                      value={newContact.teklifUcreti}
                      onChange={(e) => setNewContact({ ...newContact, teklifUcreti: e.target.value })}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Kazanılan Rakam (₺)</label>
                    <input
                      type="number"
                      value={newContact.kazanilanRakam}
                      onChange={(e) => setNewContact({ ...newContact, kazanilanRakam: e.target.value })}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Mesaj</label>
                  <textarea
                    value={newContact.message}
                    onChange={(e) => setNewContact({ ...newContact, message: e.target.value })}
                    placeholder="Mesaj (opsiyonel)"
                    rows={3}
                  />
                </div>
                <button type="submit" className={styles.submitButton}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>Anket Ekle</span>
                </button>
              </form>
            </div>
          )}

          <div className={styles.stats}>
            <div className={styles.statCard}>
              <h3>Toplam Anket</h3>
              <div className={styles.number}>{contactsStats.total}</div>
            </div>
            <div className={styles.statCard}>
              <h3>Bugün</h3>
              <div className={styles.number}>{contactsStats.today}</div>
            </div>
            <div className={styles.statCard}>
              <h3>Teklif Verilen</h3>
              <div className={styles.number}>{contactsStats.withTeklif}</div>
            </div>
            <div className={styles.statCard}>
              <h3>Kazanılan</h3>
              <div className={styles.number}>{contactsStats.withKazanilan}</div>
            </div>
          </div>

          {selectedContacts.size > 0 && (
            <div className={styles.bulkDeleteBar}>
              <span>{selectedContacts.size} anket seçildi</span>
              <button 
                className={styles.bulkDeleteButton} 
                onClick={handleBulkDelete}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                <span>Seçilenleri Sil</span>
              </button>
            </div>
          )}

          <div className={styles.tableContainer}>
            <div className={styles.tableWrapper}>
              {contactsLoading ? (
                <div className={styles.loading}>Yükleniyor...</div>
              ) : contacts.length === 0 ? (
                <div className={styles.emptyState}>Henüz anket bulunmamaktadır.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <input
                          type="checkbox"
                          checked={selectedContacts.size === contacts.length && contacts.length > 0}
                          onChange={handleSelectAll}
                          className={styles.checkbox}
                        />
                      </th>
                      <th>Tarih/Saat</th>
                      <th>İsim Soyisim</th>
                      <th>Telefon</th>
                      <th>Hizmet</th>
                      <th>Mesaj</th>
                      <th>Durum</th>
                      <th>Teklif Ücreti (₺)</th>
                      <th>Kazanılan Rakam (₺)</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => {
                      const status = contact.status || 'Aranmadı'
                      let rowClass = ''
                      if (status === 'Yarra Yedi') {
                        rowClass = styles.statusCancelled
                      } else if (status === 'Sipariş Aşamada') {
                        rowClass = styles.statusInProgress
                      } else if (status === 'Sipariş Tamamlandı') {
                        rowClass = styles.statusCompleted
                      }
                      return (
                      <tr key={contact.id} className={rowClass}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedContacts.has(contact.id)}
                            onChange={() => handleSelectContact(contact.id)}
                            className={styles.checkbox}
                          />
                        </td>
                        <td>{contact.date || (contact.timestamp ? new Date(contact.timestamp).toLocaleString('tr-TR') : '-')}</td>
                        <td><strong>{contact.name || '-'}</strong></td>
                        <td>
                          <a href={`tel:${contact.phone}`} style={{ color: '#ffffff', textDecoration: 'none', fontWeight: 600 }}>
                            {contact.phone || '-'}
                          </a>
                        </td>
                        <td>{getServiceName(contact.service) || '-'}</td>
                        <td>
                          <div 
                            className={styles.messageCell}
                            onClick={(e) => {
                              e.preventDefault()
                              if (contact.message) {
                                navigator.clipboard.writeText(contact.message)
                                const target = e.currentTarget
                                const originalText = target.textContent
                                target.textContent = 'Kopyalandı!'
                                setTimeout(() => {
                                  target.textContent = originalText
                                }, 2000)
                              }
                            }}
                            title="Mesajı kopyalamak için tıklayın"
                          >
                            {contact.message ? (
                              <span className={styles.messagePreview}>
                                {contact.message.length > 100 
                                  ? `${contact.message.substring(0, 100)}...` 
                                  : contact.message}
                              </span>
                            ) : '-'}
                          </div>
                        </td>
                        <td>
                          <select
                            className={styles.statusSelect}
                            value={contact.status || 'Aranmadı'}
                            onChange={(e) => updateContactField(contact.id, 'status', e.target.value)}
                          >
                            <option value="Aranmadı">Aranmadı</option>
                            <option value="Sipariş Aşamada">Sipariş Aşamada</option>
                            <option value="Sipariş Tamamlandı">Sipariş Tamamlandı</option>
                            <option value="Yarra Yedi">Yarra Yedi</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className={styles.amountInput}
                            value={contact.teklifUcreti || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value)
                              updateContactField(contact.id, 'teklifUcreti', value)
                            }}
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className={styles.amountInput}
                            value={contact.kazanilanRakam || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value)
                              updateContactField(contact.id, 'kazanilanRakam', value)
                            }}
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <button
                            className={styles.actionBtn}
                            onClick={() => window.location.href = `tel:${contact.phone}`}
                            title="Ara"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'grafikler' && (
        <div className={styles.grafiklerContainer}>
          <h2>Grafikler ve İstatistikler</h2>
          
          {chartData.length > 0 ? (
            <>
              <div className={styles.chartContainer}>
                <h3>Aylık Teklif ve Kazanılan Grafiği</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#ffffff"
                      style={{ fontSize: '12px', fontWeight: 600 }}
                    />
                    <YAxis 
                      stroke="#ffffff"
                      style={{ fontSize: '12px', fontWeight: 600 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K ₺`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }}
                      formatter={(value: number | undefined) => value ? `${value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}` : ''}
                    />
                    <Legend 
                      wrapperStyle={{ color: '#ffffff', fontWeight: 600 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="kazanilan" 
                      stroke="#51cf66" 
                      strokeWidth={3}
                      name="Kazanılan"
                      dot={{ fill: '#51cf66', r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="teklif" 
                      stroke="#ff6b6b" 
                      strokeWidth={3}
                      name="Teklif"
                      dot={{ fill: '#ff6b6b', r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="net" 
                      stroke="#ffd43b" 
                      strokeWidth={2}
                      name="Net Kar"
                      dot={{ fill: '#ffd43b', r: 4 }}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className={styles.chartsRow}>
                <div className={styles.chartContainer}>
                  <h3>Aylık Karşılaştırma</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#ffffff"
                        style={{ fontSize: '11px', fontWeight: 600 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        stroke="#ffffff"
                        style={{ fontSize: '12px', fontWeight: 600 }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K ₺`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff'
                        }}
                        formatter={(value: number | undefined) => value ? `${value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}` : ''}
                      />
                      <Legend 
                        wrapperStyle={{ color: '#ffffff', fontWeight: 600 }}
                      />
                      <Bar dataKey="kazanilan" fill="#51cf66" name="Kazanılan" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="teklif" fill="#ff6b6b" name="Teklif" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className={styles.chartContainer}>
                  <h3>Teklif/Kazanılan Dağılımı</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(props: any) => {
                          const { name, percent } = props;
                          return `${name || ''}: ${percent ? (percent * 100).toFixed(0) : 0}%`;
                        }}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff'
                        }}
                        formatter={(value: number | undefined) => value ? `${value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}` : ''}
                      />
                      <Legend 
                        wrapperStyle={{ color: '#ffffff', fontWeight: 600 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>Henüz grafik verisi bulunmamaktadır.</div>
          )}
        </div>
      )}

      {activeTab === 'finansal' && (
        <div className={styles.finansalContainer}>
          <h2>Finansal Özet</h2>
          
          <div className={styles.statsGrid}>
            <div className={styles.statCardLarge}>
              <h3>Toplam Teklif</h3>
              <div className={styles.statNumber} style={{ color: '#ff6b6b' }}>
                {totalTeklif.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
              </div>
            </div>
            <div className={styles.statCardLarge}>
              <h3>Toplam Kazanılan</h3>
              <div className={styles.statNumber} style={{ color: '#51cf66' }}>
                {totalKazanilan.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
              </div>
            </div>
            <div className={styles.statCardLarge}>
              <h3>Net Kar/Zarar</h3>
              <div className={styles.statNumber} style={{ color: netKar >= 0 ? '#51cf66' : '#ff6b6b' }}>
                {netKar.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
              </div>
            </div>
          </div>

          <div className={styles.monthlyStatsContainer}>
            <h3>Aylık İstatistikler</h3>
            {Object.keys(monthlyStats).length === 0 ? (
              <div className={styles.emptyState}>Henüz aylık veri bulunmamaktadır.</div>
            ) : (
              <div className={styles.monthlyStatsGrid}>
                {Object.entries(monthlyStats)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([month, stats]) => {
                    const [year, monthNum] = month.split('-')
                    const monthName = new Date(parseInt(year), parseInt(monthNum) - 1).toLocaleString('tr-TR', { month: 'long', year: 'numeric' })
                    const profit = stats.kazanilan - stats.teklif
                    
                    return (
                      <div key={month} className={styles.monthlyStatCard}>
                        <h4>{monthName}</h4>
                        <div className={styles.monthlyStatRow}>
                          <span>Teklif:</span>
                          <strong style={{ color: '#ff6b6b' }}>
                            {stats.teklif.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                          </strong>
                        </div>
                        <div className={styles.monthlyStatRow}>
                          <span>Kazanılan:</span>
                          <strong style={{ color: '#51cf66' }}>
                            {stats.kazanilan.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                          </strong>
                        </div>
                        <div className={styles.monthlyStatRow}>
                          <span>Net:</span>
                          <strong style={{ color: profit >= 0 ? '#51cf66' : '#ff6b6b' }}>
                            {profit.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                          </strong>
                        </div>
                      </div>
                    )
                  })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin

